import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import inviteService from '../../services/inviteService';
import { shareLink } from '../../utils/nativeShare';
import { contactsSource, loadContacts, ContactsDenied } from '../../utils/phoneContacts';
import { smsHref, cleanNumber, inviteSmsBody, MAX_SMS_RECIPIENTS } from '../../utils/smsCompose';
import { matchContacts, toE164 } from '../../utils/contactMatch';
import usePageScrollLock from '../../utils/usePageScrollLock';
import { RING } from '../wall/wallTokens';
import { XIcon } from '../portal/canvas/icons';
import ContactPickList from '../onboarding/ContactPickList';
import { CYAN, MiniPill, SectionCard } from '../onboarding/onboardingUi';

/**
 * INVITE FRIENDS (People page, Oct 2026; sign-up v3 styling Oct 22 2026) — your invite-card link,
 * texted to people from your own phone.
 *
 * The same vocabulary as the sign-up's CONTACTS step: a #0B0B0B sheet, numbered steps, the link
 * in a 50px black pill with COPY LINK, the book as pick rows (40px initials disc · name ·
 * `250 ··· 0143 · NOT ON PORTAL` / `ON PORTAL` · 22px checkbox) under a search pill, a typed-number
 * pill, a ring-bordered SEND BY TEXT and two round secondaries. Step 1 is your `/i/{code}` link,
 * copied the moment the sheet opens (the People page copies it inside the tap, while the gesture
 * is still live — see `useInviteLink`). Step 2 picks people: the phone's address book in the
 * native shells (the OS permission prompt, then search + tick), the OS contact picker on Chrome
 * for Android, or a number typed in. Who is already on Portal is learned by hash
 * (`matchContacts`) — the book itself never leaves the phone. Step 3 opens Messages with everyone
 * filled in — the text goes from YOUR number, never Portal's, so it reads as a text from a friend.
 */

const cache = { promise: null, link: null };

/** The signed-in person's link, fetched once per session and shared by every opener. */
export function useInviteLink() {
  const [link, setLink] = useState(cache.link);
  useEffect(() => {
    let on = true;
    fetchInviteLink().then((l) => { if (on && l) setLink(l); });
    return () => { on = false; };
  }, []);
  return link;
}

export async function fetchInviteLink() {
  if (cache.link) return cache.link;
  if (!cache.promise) {
    cache.promise = inviteService.mine().then((r) => {
      if (r?.success && r.data?.url) { cache.link = { code: r.data.code, url: r.data.url, name: r.data.ownerName || '' }; return cache.link; }
      cache.promise = null;
      return null;
    }).catch(() => { cache.promise = null; return null; });
  }
  return cache.promise;
}

export function resetInviteLinkCache() { cache.promise = null; cache.link = null; }

export async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

const linkLabel = (url) => (url || '').replace(/^https?:\/\//, '');

/** One row per contact: their first number a text can reach (E.164 when it can be one). */
export function rowsFromBook(contacts) {
  return (contacts || []).map((c) => {
    const first = (c.phones || []).find((p) => toE164(p.number)) || (c.phones || [])[0];
    if (!first) return null;
    const e164 = toE164(first.number) || '';
    return { contactId: c.id, name: c.name, number: e164 || cleanNumber(first.number), e164 };
  }).filter(Boolean);
}

const Step = ({ n, done, right, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
    <span style={{ width: 22, height: 22, flexShrink: 0, borderRadius: '50%', background: done ? CYAN : '#fff', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, transition: 'background-color .2s' }}>{done ? '✓' : n}</span>
    <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em', minWidth: 0, flex: 1 }}>{children}</span>
    {right}
  </div>
);

const circle = { height: 52, width: 52, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, background: '#161616', color: '#fff', border: 0, borderRadius: '50%', cursor: 'pointer', transition: 'background-color .2s' };
const ringButton = { height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: `linear-gradient(#000,#000) padding-box, ${RING} border-box`, border: '3px solid transparent', color: '#fff', borderRadius: 999, fontFamily: 'inherit', fontSize: 12, fontWeight: 800, letterSpacing: '.16em', cursor: 'pointer', minWidth: 0 };
const bareField = { flex: 1, minWidth: 0, height: '100%', padding: 0, background: 'transparent', border: 0, boxShadow: 'none', color: '#fff', fontFamily: 'inherit', fontSize: 14, outline: 'none' };

export default function InviteFriendsSheet({ open, onClose, copiedAtStart = false, link: linkProp = null }) {
  const [link, setLink] = useState(linkProp || cache.link);
  const [copied, setCopied] = useState(copiedAtStart);
  const [contacts, setContacts] = useState(null);     // null until the book has been read
  const [onPortal, setOnPortal] = useState(() => new Map()); // contactId → match
  const [picked, setPicked] = useState(() => new Map()); // number → { name, number }
  const [query, setQuery] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');             // a neutral line: what the picker came back with
  const [sent, setSent] = useState(null);             // 'sms' | 'share' | null
  const [kbOffset, setKbOffset] = useState(0);
  const source = useMemo(() => contactsSource(), []);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  usePageScrollLock(open);

  // Fresh each time it opens: the link (and the copy, if the opener couldn't do it in the tap).
  useEffect(() => {
    if (!open) return undefined;
    setCopied(copiedAtStart); setError(''); setHint(''); setSent(null);
    if (linkProp) setLink(linkProp);
    let on = true;
    fetchInviteLink().then(async (l) => {
      if (!on) return;
      if (!l) { setError('COULDN’T GET YOUR LINK · CHECK YOUR CONNECTION'); return; }
      setLink(l);
      if (!copiedAtStart && await copyText(l.url) && on) setCopied(true);
    });
    return () => { on = false; };
  }, [open, copiedAtStart, linkProp]);

  // The keyboard covers a fixed sheet on a phone; follow the visual viewport up.
  useEffect(() => {
    if (!open) return undefined;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;
    const sync = () => setKbOffset(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    sync();
    vv.addEventListener('resize', sync); vv.addEventListener('scroll', sync);
    return () => { vv.removeEventListener('resize', sync); vv.removeEventListener('scroll', sync); setKbOffset(0); };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const rows = useMemo(() => rowsFromBook(contacts), [contacts]);
  const pickedIds = useMemo(() => new Set(rows.filter((r) => picked.has(r.number)).map((r) => r.contactId)), [rows, picked]);

  if (!open || typeof document === 'undefined') return null;

  const url = link?.url || '';
  const body = inviteSmsBody({ name: link?.name, url });
  const people = [...picked.values()];
  const full = people.length >= MAX_SMS_RECIPIENTS;

  const copy = async () => {
    if (!url) return;
    if (await copyText(url)) { setCopied(true); setError(''); } else setError('COULDN’T COPY · LONG-PRESS THE LINK TO COPY IT');
  };

  const toggle = (person) => {
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(person.number)) next.delete(person.number);
      else if (next.size < MAX_SMS_RECIPIENTS) next.set(person.number, person);
      return next;
    });
    setSent(null); setHint('');
  };

  /** Who in the book is already here — by hash, after the list is up; a failure just leaves the tags off. */
  const learnWhoIsHere = async (book) => {
    try {
      const { matches } = await matchContacts(book);
      if (!alive.current) return;
      setOnPortal(new Map(matches.map((m) => [m.contact.contactId, m.user])));
    } catch { /* the list works without the tags */ }
  };

  const pick = async () => {
    setBusy(true); setError(''); setHint('');
    try {
      const { people: book, received, cancelled } = await loadContacts();
      if (!alive.current) return;
      if (source === 'picker') {
        // The OS picker already did the choosing: everything it handed back is picked. Every
        // outcome says something — a picker that closes to a silent sheet reads as broken.
        if (cancelled || !received) setHint('NOBODY PICKED · TRY AGAIN, OR TYPE A NUMBER');
        else if (!book.length) setError(`${received === 1 ? 'THAT CONTACT' : 'THOSE CONTACTS'} CAME WITHOUT A PHONE NUMBER · SHARE NUMBERS TOO, OR TYPE ONE`);
        else rowsFromBook(book).forEach((r) => toggle({ name: r.name, number: r.number }));
      } else {
        setContacts(book);
        if (!book.length) setError('NO CONTACTS WITH A PHONE NUMBER ON THIS PHONE');
        else learnWhoIsHere(book);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[invite] contacts', e?.name, e?.message);
      if (!alive.current) return;
      setError(e instanceof ContactsDenied ? 'CONTACTS ARE OFF FOR PORTAL · TURN THEM ON IN SETTINGS, OR TYPE A NUMBER' : 'COULDN’T READ YOUR CONTACTS · TYPE A NUMBER INSTEAD');
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const addTyped = () => {
    const number = toE164(typed) || cleanNumber(typed);
    if (!number) { setError('THAT DOESN’T LOOK LIKE A PHONE NUMBER'); return; }
    toggle({ name: number, number });
    setTyped(''); setError('');
  };

  const sendText = () => {
    if (!people.length || !url) return;
    const href = smsHref(people.map((p) => p.number), body);
    setError('');
    try { window.location.href = href; setSent('sms'); } catch { setError('COULDN’T OPEN MESSAGES'); }
  };

  const share = async () => {
    if (!url) return;
    try {
      if (await shareLink({ title: 'Portal', text: body, url })) setSent('share');
      else await copy();
    } catch { setError('COULDN’T OPEN YOUR SHARE SHEET'); }
  };

  const note = error
    || (sent === 'sms' ? `OPENED MESSAGES · ${people.length} ${people.length === 1 ? 'PERSON' : 'PEOPLE'} · HIT SEND THERE`
      : sent === 'share' ? 'OPENED YOUR SHARE SHEET · PICK A CHAT'
        : people.length ? `${people.length} PICKED${full ? ' · THAT’S THE MOST ONE TEXT CAN TAKE' : ''}`
          : hint || (copied ? 'LINK COPIED · PICK WHO TO TEXT IT TO' : 'YOUR LINK IS READY'));
  const noteColor = error ? '#FF66C4' : sent || people.length ? CYAN : '#6B6B6B';
  const hereCount = onPortal.size;

  return createPortal(
    <div className="pr-overlay-root pk" data-testid="invite-friends" style={{ position: 'fixed', inset: 0, zIndex: 80, color: '#fff' }}>
      <div onClick={onClose} aria-hidden style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.6)' }} />
      <div role="dialog" aria-label="Invite friends" aria-modal="true" style={{ position: 'absolute', left: 0, right: 0, bottom: kbOffset, maxHeight: `calc(92% - ${kbOffset}px)`, maxWidth: 560, margin: '0 auto', background: '#0B0B0B', borderRadius: '28px 28px 0 0', boxShadow: 'inset 0 1px 0 #1F1F1F', padding: `16px 18px calc(18px + ${kbOffset ? '0px' : 'env(safe-area-inset-bottom, 0px)'})`, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', WebkitOverflowScrolling: 'touch', animation: 'pifSlideUp .44s cubic-bezier(.2,.7,.2,1) both' }}>
        <style>{'@keyframes pifSlideUp { from { transform: translateY(100%) } to { transform: none } } @media (prefers-reduced-motion: reduce) { [data-testid="invite-friends"] [role="dialog"] { animation: none !important } }'}</style>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>INVITE FRIENDS</span>
          <button type="button" onClick={onClose} aria-label="Close" className="pk-btn pk-secondary-on-black" style={{ width: 40, height: 40, padding: 0, borderRadius: '50%' }}><XIcon size={12} /></button>
        </div>

        <Step n={1} done={copied}>YOUR INVITE LINK</Step>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 50, padding: '0 6px 0 20px', background: '#000', borderRadius: 999, boxShadow: 'inset 0 0 0 1px #2A2A2A' }}>
          <span data-testid="invite-link" style={{ flex: 1, minWidth: 0, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', userSelect: 'text', WebkitUserSelect: 'text', color: url ? '#fff' : '#5A5A5A' }}>{url ? linkLabel(url) : 'GETTING YOUR LINK…'}</span>
          <MiniPill bg={copied ? CYAN : '#fff'} fg="#000" height={38} hot={!copied} onClick={copy} disabled={!url} data-testid="copy-link">{copied ? 'COPIED' : 'COPY LINK'}</MiniPill>
        </div>
        <span style={{ fontSize: 12, lineHeight: 1.55, color: '#8A8A8A' }}>Whoever signs up through it lands on your Portal Page, and Portal remembers you brought them. Same link as your printed invite card.</span>

        <Step n={2} done={people.length > 0} right={<span data-testid="pick-count" style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.14em', whiteSpace: 'nowrap', color: people.length ? CYAN : '#6B6B6B' }}>{people.length ? `${people.length} OF ${MAX_SMS_RECIPIENTS}` : `UP TO ${MAX_SMS_RECIPIENTS}`}</span>}>PICK WHO TO TEXT</Step>
        {source !== 'none' && (
          <button type="button" onClick={pick} disabled={busy} data-testid="pick-contacts" className={`pk-btn ${contacts ? '' : 'pk-primary-on-black'}`} style={{ height: 50, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: contacts ? '#111' : '#fff', color: contacts ? '#fff' : '#000', boxShadow: contacts ? 'inset 0 0 0 1px #1F1F1F' : 'none', fontSize: 11, fontWeight: 800, letterSpacing: '.16em', opacity: busy ? 0.6 : 1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" /><circle cx="17" cy="9" r="2.5" /><path d="M16 14.5c3 0 5.5 2.5 5.5 5.5" /></svg>
            {busy ? 'READING YOUR CONTACTS…' : contacts ? `CONTACTS · ${rows.length}${hereCount ? ` · ${hereCount} ON PORTAL` : ''}` : 'PICK FROM CONTACTS'}
          </button>
        )}
        {contacts && rows.length > 0 && (
          <SectionCard gap={10} style={{ padding: 14, borderRadius: 22 }}>
            <ContactPickList
              rows={rows}
              picked={pickedIds}
              onToggle={(r) => toggle({ name: r.name, number: r.number })}
              cap={MAX_SMS_RECIPIENTS}
              query={query}
              onQuery={setQuery}
              maxHeight={260}
              tagFor={(r) => (onPortal.has(r.contactId) ? 'ON PORTAL' : 'NOT ON PORTAL')}
            />
          </SectionCard>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 50, padding: '0 6px 0 20px', background: '#000', borderRadius: 999, boxShadow: 'inset 0 0 0 1px #2A2A2A' }}>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTyped(); } }} inputMode="tel" autoComplete="tel" placeholder={source === 'none' ? 'Type a phone number' : 'Or type a number'} aria-label="Phone number" className="pe-in" style={bareField} />
          <MiniPill bg="#fff" fg="#000" height={38} hot onClick={addTyped} data-testid="add-number">+ ADD</MiniPill>
        </div>
        {people.length > 0 && (
          <div data-testid="picked" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {people.map((p) => (
              <button key={p.number} type="button" onClick={() => toggle(p)} aria-label={`Remove ${p.name}`} style={{ display: 'flex', alignItems: 'center', gap: 6, height: 30, padding: '0 10px 0 14px', background: '#fff', color: '#000', border: 0, borderRadius: 999, fontFamily: 'inherit', fontSize: 10, fontWeight: 800, letterSpacing: '.1em', cursor: 'pointer', maxWidth: '100%' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name.toUpperCase()}</span><XIcon size={9} />
              </button>
            ))}
          </div>
        )}

        <Step n={3} done={!!sent}>SEND</Step>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 52px 52px', gap: 8, opacity: url ? 1 : 0.4, transition: 'opacity .2s' }} data-testid="invite-actions">
          <button type="button" onClick={sendText} disabled={!people.length || !url} data-testid="send-text" style={{ ...ringButton, opacity: people.length ? 1 : 0.45 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden><path d="M4 4h16v12H9l-5 4z" /></svg>
            {people.length ? `SEND BY TEXT · ${people.length}` : 'SEND BY TEXT'}
          </button>
          <button type="button" onClick={share} aria-label="Share the link another way" title="Share" className="pk-btn pk-secondary-on-black" style={circle}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden><path d="M12 15V4M7 9l5-5 5 5" /><path d="M5 13v7h14v-7" /></svg></button>
          <button type="button" onClick={copy} aria-label="Copy the link" title="Copy" className="pk-btn pk-secondary-on-black" style={circle}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden><rect x="8" y="8" width="12" height="12" /><path d="M16 8V4H4v12h4" /></svg></button>
        </div>
        <span data-testid="invite-note" style={{ textAlign: 'center', fontSize: 10, fontWeight: 800, letterSpacing: '.14em', color: noteColor }}>{note}</span>
      </div>
    </div>,
    document.body,
  );
}
